// ─── YouTube and Vimeo links, as players ──────────────────────────────────────
// The visual editor turns a pasted YouTube or Vimeo link into that service's
// player. The server keeps an <iframe> in a saved page only when it is one of
// these players (builderslab/server/services/videoEmbed.js), so the two must
// agree on every link: both are tested against one list,
// builderslab/server/__tests__/fixtures/videoLinks.json.

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^\d{6,12}$/;
// Vimeo paths that carry a number but are not one video.
const VIMEO_NOT_A_VIDEO = new Set(['showcase', 'album', 'user', 'event', 'ondemand', 'manage', 'categories']);

/** What the players need: full screen, picture in picture, and autoplay when muted. */
export const EMBED_ALLOW = 'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share';

/** "90", "90s", "1m30s", "1h2m" → seconds; anything else → 0. */
function seconds(t) {
  const s = String(t || '').trim();
  if (/^\d+$/.test(s)) return Number(s);
  const m = s.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!m || !s) return 0;
  return (Number(m[1] || 0) * 3600) + (Number(m[2] || 0) * 60) + Number(m[3] || 0);
}

/**
 * The player address for a YouTube or Vimeo link, or null when it is neither.
 *
 * @param {string} link
 * @returns {{provider: 'youtube'|'vimeo', id: string, src: string}|null}
 */
export function videoEmbed(link) {
  let raw = String(link || '').trim();
  if (raw.startsWith('//')) raw = `https:${raw}`;
  let u;
  try { u = new URL(raw); } catch { return null; }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;

  const host = u.hostname.toLowerCase().replace(/^(www\.|m\.|music\.)/, '');
  const parts = u.pathname.split('/').filter(Boolean);

  let id = null;
  if (host === 'youtu.be') id = parts[0];
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (parts[0] === 'watch') id = u.searchParams.get('v');
    else if (['embed', 'shorts', 'live', 'v'].includes(parts[0])) id = parts[1];
  }
  if (id && YOUTUBE_ID.test(id)) {
    const start = seconds(u.searchParams.get('start') || u.searchParams.get('t'));
    return { provider: 'youtube', id, src: `https://www.youtube-nocookie.com/embed/${id}${start ? `?start=${start}` : ''}` };
  }

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    if (VIMEO_NOT_A_VIDEO.has(parts[0])) return null;
    const at = parts.findIndex(p => VIMEO_ID.test(p));
    if (at < 0) return null;
    const vid = parts[at];
    const hash = u.searchParams.get('h') || parts[at + 1] || '';
    const h = /^[0-9a-f]{6,20}$/i.test(hash) ? hash : '';
    return { provider: 'vimeo', id: vid, src: `https://player.vimeo.com/video/${vid}${h ? `?h=${h}` : ''}` };
  }
  return null;
}
